import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router";
import {
  Badge,
  Box,
  Button,
  Drawer,
  IconButton,
  Popover,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Bell, Trash } from "lucide-react";
import { observer } from "mobx-react-lite";

import { isAllowedProcessNotificationRoute } from "@/components/ProcessNotificationBridge/isAllowedProcessNotificationRoute";
import { NOTIFICATION_DELIVERY, useNotification, SEVERITY_META } from "@/context/NotificationContext";
import { dispatchInboxNotificationAction, hasInboxNotificationAction } from "@/context/notificationActionRegistry";
import useStore from "@/hooks/useStore";
import { APP_BAR_WHITE_ICON_BUTTON_SX } from "@/theme/appTheme";

/**
 * Syncs persisted inbox scope with the same principal as GraphQL `user_id` (spec §6).
 * Uses facade user id so notifications follow the effective principal.
 */
export const NotificationInboxScopeSync = observer(function NotificationInboxScopeSync() {
  const { authStore } = useStore();
  const { setNotificationInboxScope } = useNotification();
  const inboxUserId = authStore.userId || "";
  const inboxCustomer = authStore.customer || "";

  useEffect(() => {
    setNotificationInboxScope({
      userId: inboxUserId,
      customer: inboxCustomer,
    });
  }, [inboxUserId, inboxCustomer, setNotificationInboxScope]);

  return null;
});

function formatRelativeTimestamp(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function NotificationPanelList({
  items,
  onMarkAllRead,
  onClearAll,
  emptyLabel,
  onNotificationClick,
  onDeleteNotification,
  onRouteLinkPrepare,
}) {
  if (items.length === 0) {
    return (
      <Box sx={{ px: 3, py: 6, textAlign: "center" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            mx: "auto",
            mb: 1.5,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(157, 106, 196, 0.1)",
            color: "#9D6AC4",
          }}
        >
          <Bell size={26} aria-hidden />
        </Box>
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "text.primary", mb: 0.5 }}>All caught up!</Typography>
        <Typography sx={{ fontSize: "0.95rem", color: "text.secondary" }}>{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflow: "hidden" }}>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map((item) => {
          const meta = SEVERITY_META[item.severity] ?? SEVERITY_META.info;
          const cardTitle =
            typeof item.title === "string" && item.title.trim().length > 0
              ? item.title.trim()
              : item.severity === "success"
                ? "Strategy Ready"
                : "Notification";
          const subtitle =
            typeof item.propertyAddress === "string" && item.propertyAddress.trim().length > 0
              ? item.propertyAddress.trim()
              : typeof item.address === "string" && item.address.trim().length > 0
                ? item.address.trim()
                : item.message;
          const ts = formatRelativeTimestamp(item.createdAt || item.timestamp || item.created_at);
          const hasActionIds =
            Array.isArray(item.actionIds) && item.actionIds.some((aid) => hasInboxNotificationAction(aid));
          const allowedRoute =
            typeof item.actionRoute === "string" &&
            item.actionRoute.length > 0 &&
            isAllowedProcessNotificationRoute(item.actionRoute)
              ? item.actionRoute
              : null;
          const isClickable = Boolean(allowedRoute || hasActionIds);
          const useRouterLinkForCta = Boolean(allowedRoute) && !hasActionIds;
          const ctaLabel =
            typeof item.actionLabel === "string" && item.actionLabel.trim().length > 0
              ? item.actionLabel.trim()
              : "View Property";
          const ctaSx = {
            mt: 0.5,
            px: 0,
            py: 0,
            border: "none",
            bgcolor: "transparent",
            textAlign: "left",
            cursor: "pointer",
            color: "#9D6AC4",
            fontSize: 12,
            fontWeight: 600,
            textTransform: "none",
            lineHeight: 1.25,
            alignSelf: "flex-start",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
              borderRadius: "4px",
            },
          };
          return (
            <Box
              key={item.id}
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : -1}
              onClick={isClickable ? () => onNotificationClick(item) : undefined}
              onKeyDown={
                isClickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onNotificationClick(item);
                      }
                    }
                  : undefined
              }
              sx={{
                position: "relative",
                p: 2,
                pl: 2.75,
                borderRadius: "12px",
                bgcolor: item.read ? "rgba(249, 250, 251, 0.95)" : "rgba(243, 240, 255, 0.72)",
                border: "1px solid rgba(0, 0, 0, 0.04)",
                cursor: isClickable ? "pointer" : "default",
                transition: "background-color 150ms ease, box-shadow 150ms ease",
                "&:hover": isClickable
                  ? {
                      bgcolor: item.read ? "rgba(249, 250, 251, 1)" : "rgba(243, 240, 255, 0.88)",
                      boxShadow: "0 6px 14px rgba(15, 23, 42, 0.08)",
                    }
                  : undefined,
                "&:focus-visible": isClickable
                  ? {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: 2,
                    }
                  : undefined,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: item.read ? 2 : 4,
                  height: "70%",
                  borderRadius: "4px",
                  bgcolor: item.read ? "rgba(156, 163, 175, 0.9)" : meta.railColor,
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: item.read ? 600 : 700, color: "text.primary", lineHeight: 1.35 }}>
                    {cardTitle}
                  </Typography>
                  <Typography sx={{ fontSize: "0.84rem", color: "text.secondary", lineHeight: 1.45, mt: 0.5 }}>
                    {subtitle}
                  </Typography>
                  {isClickable ? (
                    useRouterLinkForCta ? (
                      <Typography
                        component={RouterLink}
                        to={allowedRoute}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRouteLinkPrepare(item);
                        }}
                        sx={ctaSx}
                      >
                        {ctaLabel}
                      </Typography>
                    ) : (
                      <Typography
                        component="button"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNotificationClick(item);
                        }}
                        sx={ctaSx}
                      >
                        {ctaLabel}
                      </Typography>
                    )
                  ) : null}
                  {ts ? (
                    <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap", mt: 0.5 }}>
                      {ts}
                    </Typography>
                  ) : null}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  aria-label="Delete notification"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteNotification?.(item.id);
                  }}
                  sx={{
                    color: "text.secondary",
                    flexShrink: 0,
                    alignSelf: "center",
                    "&:hover": { color: "error.main", bgcolor: "rgba(220, 38, 38, 0.08)" },
                  }}
                >
                  <Trash size={18} strokeWidth={2} aria-hidden />
                </IconButton>
              </Box>
            </Box>
          );
          })}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 1.75,
          borderTop: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 -4px 10px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Button size="small" color="inherit" onClick={onClearAll} sx={{ textTransform: "none", fontWeight: 600, fontSize: 14 }}>
          Clear all
        </Button>
        <Button size="small" onClick={onMarkAllRead} sx={{ textTransform: "none", fontWeight: 600, fontSize: 14 }}>
          Mark all read
        </Button>
      </Box>
    </Box>
  );
}

/**
 * App bar notification bell + inbox panel (popover from `sm` and up; bottom drawer below `sm`, 600px by default). Spec: NOTIFICATION_CENTER_AND_DIALOGS_SPEC §5.
 *
 * @param {{ iconButtonSx?: import("@mui/material").IconButtonProps["sx"] }} [props]
 */
export default function NotificationBell({ iconButtonSx }) {
  const theme = useTheme();
  const isMobilePanel = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const {
    inboxItems,
    unreadInboxCount,
    dismissInboxNotification,
    markInboxNotificationRead,
    markAllInboxNotificationsRead,
    clearAllInboxNotifications,
    restoreInboxNotifications,
    notify,
    hide,
  } = useNotification();

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tabValue, setTabValue] = useState("all");

  const tooltipTitle = unreadInboxCount > 0 ? `Notifications, ${unreadInboxCount} unread` : "Notifications, no unread items";
  const ariaLabel = unreadInboxCount > 0 ? `Notifications, ${unreadInboxCount} unread` : "Notifications, no unread items";

  const handleBellClick = useCallback(
    (event) => {
      if (isMobilePanel) {
        setMobileOpen(true);
      } else {
        setAnchorEl(event.currentTarget);
      }
    },
    [isMobilePanel]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setMobileOpen(false);
  }, []);

  const handleClearAll = useCallback(() => {
    if (!Array.isArray(inboxItems) || inboxItems.length === 0) return;
    const backup = inboxItems;
    clearAllInboxNotifications();
    notify.rich(
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.92rem",
              letterSpacing: "0.01em",
              fontWeight: 600,
              color: "var(--gray-500, #555555)",
              lineHeight: 1.25,
            }}
          >
            {SEVERITY_META.info.title}
          </Typography>
          <Typography
            sx={{
              fontSize: "1.125rem",
              fontWeight: 500,
              color: "var(--foreground, #2D3748)",
              lineHeight: 1.38,
            }}
          >
            All notifications cleared.
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => {
            restoreInboxNotifications(backup);
            hide();
          }}
          sx={{
            color: "var(--primary, #9D6AC4)",
            fontWeight: 700,
            textTransform: "uppercase",
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          Undo
        </Button>
      </Box>,
      "info",
      { delivery: NOTIFICATION_DELIVERY.SNACKBAR_ONLY }
    );
  }, [clearAllInboxNotifications, hide, inboxItems, notify, restoreInboxNotifications]);

  const handleNotificationClick = useCallback(
    (item) => {
      const route = item?.actionRoute;
      const actionIds = Array.isArray(item?.actionIds) ? item.actionIds.filter((aid) => hasInboxNotificationAction(aid)) : [];
      if (actionIds.length > 0) {
        dispatchInboxNotificationAction(actionIds[0], { itemId: item.id });
        markInboxNotificationRead(item.id);
        return;
      }
      if (typeof route === "string" && route.length > 0 && isAllowedProcessNotificationRoute(route)) {
        markInboxNotificationRead(item.id);
        handleClose();
        navigate(route);
      } else if (item?.id) {
        markInboxNotificationRead(item.id);
      }
    },
    [handleClose, markInboxNotificationRead, navigate]
  );

  const prepareRouteLinkFollow = useCallback(
    (item) => {
      markInboxNotificationRead(item.id);
      handleClose();
    },
    [handleClose, markInboxNotificationRead]
  );

  const emptyCopy = "You're all caught up.";
  const unreadCount = useMemo(() => inboxItems.filter((item) => item.read !== true).length, [inboxItems]);
  const filteredInboxItems = useMemo(
    () => (tabValue === "unread" ? inboxItems.filter((item) => item.read !== true) : inboxItems),
    [inboxItems, tabValue]
  );
  const emptyLabel = tabValue === "unread" ? "No unread notifications." : emptyCopy;

  const panelHeader = (
    <Box
      sx={{
        p: 3,
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "1.21rem", fontFamily: "'Inter', sans-serif", textAlign: "center" }}>
        Notifications
      </Typography>
      <IconButton
        size="small"
        onClick={handleClose}
        aria-label="Close notifications"
        sx={{ color: "var(--gray-500, #555555)", position: "absolute", right: 20 }}
      >
        <CloseRoundedIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Box>
  );

  const panelBody = (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
      {panelHeader}
      <Box
        sx={{
          px: 2,
          pt: 1,
          pb: 1,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          mb: 0.5,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, value) => setTabValue(value)}
          variant="fullWidth"
          aria-label="Notification filters"
          sx={{
            minHeight: 38,
            "& .MuiTabs-indicator": { backgroundColor: "#9D6AC4" },
          }}
        >
          <Tab
            value="all"
            label="All"
            sx={{
              minHeight: 38,
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              "&.Mui-selected": { color: "#9D6AC4" },
            }}
          />
          <Tab
            value="unread"
            label={`Unread (${unreadCount})`}
            sx={{
              minHeight: 38,
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              "&.Mui-selected": { color: "#9D6AC4" },
            }}
          />
        </Tabs>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <NotificationPanelList
          items={filteredInboxItems}
          onMarkAllRead={() => {
            markAllInboxNotificationsRead();
          }}
          onClearAll={handleClearAll}
          emptyLabel={emptyLabel}
          onNotificationClick={handleNotificationClick}
          onDeleteNotification={dismissInboxNotification}
          onRouteLinkPrepare={prepareRouteLinkFollow}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <IconButton
          onClick={handleBellClick}
          aria-label={ariaLabel}
          sx={iconButtonSx ?? APP_BAR_WHITE_ICON_BUTTON_SX}
        >
          <Badge
            overlap="circular"
            color="error"
            invisible={unreadInboxCount === 0}
            badgeContent={unreadInboxCount > 9 ? "9+" : unreadInboxCount}
            sx={{
              "& .MuiBadge-badge": {
                fontWeight: 700,
                fontSize: "0.787rem",
                minWidth: 20,
                height: 20,
                transition: "transform 0.2s ease, opacity 0.2s ease",
              },
            }}
          >
            <Bell size={22} color={iconButtonSx ? "currentColor" : "#9D6AC4"} strokeWidth={2} aria-hidden />
          </Badge>
        </IconButton>
      </Tooltip>

      {isMobilePanel ? (
        <Drawer
          anchor="bottom"
          open={mobileOpen}
          onClose={handleClose}
          sx={{
            zIndex: 1400,
            "& .MuiDrawer-paper": {
              zIndex: 1400,
            },
          }}
          slotProps={{
            paper: {
              sx: {
                height: "80vh",
                borderRadius: "16px 16px 0 0",
                maxHeight: "80vh",
                display: "flex",
                flexDirection: "column",
              },
            },
            backdrop: {
              sx: {
                backgroundColor: "rgba(0,0,0,0.5)",
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
              },
            },
          }}
          ModalProps={{
            keepMounted: false,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 572, mx: "auto", height: "100%", minHeight: 0 }}>{panelBody}</Box>
        </Drawer>
      ) : (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                width: "min(440px, calc(100vw - 26px))",
                height: "calc(100dvh - 88px)",
                maxHeight: "calc(100dvh - 88px)",
                mt: 1.1,
                borderRadius: 2,
                boxShadow: "0 12px 40px rgba(15, 23, 42, 0.12)",
                border: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              },
            },
          }}
        >
          {panelBody}
        </Popover>
      )}
    </>
  );
}
